import type {
  ProductKnowledgeDetailByType,
  ProductKnowledgeDetailPage,
  ProductKnowledgeProductPage,
  ProductKnowledgeProductSearchInput,
  ProductKnowledgeScopeMutationInput,
  ProductKnowledgeScopeSearchInput,
  ProductKnowledgeScopeSearchResult,
  ProductKnowledgeScopeSnapshot,
  ProductKnowledgeSearchInput,
  ProductKnowledgeType,
  ProductKnowledgeUpdateInputByType,
  ProductKnowledgePickerInput,
} from '../model/types';
import { knowledgeGet, knowledgePost, knowledgePut } from './http';
import { queryItemList } from '@knowledge/common/components/ItemSelectV2/api';

const productBase = '/oversea-knowledge/product_knowledge';

const knowledgeBaseByType: Record<ProductKnowledgeType, string> = {
  SUPPLEMENTAL_KNOWLEDGE: '/oversea-knowledge/supplemental_knowledge/knowledge',
  DOCUMENT_KNOWLEDGE: '/oversea-knowledge/document_knowledge/knowledge',
  SPU_DETAIL_KNOWLEDGE: '/oversea-knowledge/spu_detail_v2/knowledge',
};

const normalizeKnowledgeId = (type: ProductKnowledgeType, knowledgeId: string | number) =>
  type === 'DOCUMENT_KNOWLEDGE' ? knowledgeId : String(knowledgeId);

const getKnowledgePath = (type: ProductKnowledgeType, knowledgeId: string | number) =>
  `${knowledgeBaseByType[type]}/${normalizeKnowledgeId(type, knowledgeId)}`;

/** 商品信息补全的批量大小（Item V4 spuKey 精确查询，200/批） */
const PRODUCT_INFO_BATCH_SIZE = 200;

const normalizeProductSpuKey = (spuId: string, platform: string, region: string) =>
  `${spuId}|${platform.toUpperCase()}|${region.toUpperCase()}`;

interface MissingProductInfo {
  externalStoreId: string;
  itemUrl?: string;
  imageUrl?: string;
}

/** 商品列表异步补全（对齐 perseus adapter searchProductKnowledgeProducts）：
 *  搜索结果缺失外链/图片的商品，用 Item V4 spuKey 批量查询回填；失败不阻断列表，刷新可重试 */
const enrichProductPage = async (
  page: ProductKnowledgeProductPage,
): Promise<ProductKnowledgeProductPage> => {
  const missing = page.items.filter((item) => !item.itemUrl || !item.imageUrl);
  if (!missing.length) return page;
  try {
    const spuKeys = missing.map((item) =>
      normalizeProductSpuKey(item.externalSpuId, item.platform, item.region),
    );
    const responses = await Promise.all(
      Array.from({ length: Math.ceil(spuKeys.length / PRODUCT_INFO_BATCH_SIZE) }, (_, index) =>
        queryItemList({
          storeType: 0,
          keywordType: 2,
          keyword: '',
          includeUnlisted: true,
          spuKey: spuKeys.slice(
            index * PRODUCT_INFO_BATCH_SIZE,
            (index + 1) * PRODUCT_INFO_BATCH_SIZE,
          ),
          pageNo: 1,
          pageSize: PRODUCT_INFO_BATCH_SIZE,
        }),
      ),
    );
    const detailByKey = new Map<string, MissingProductInfo>();
    responses
      .flatMap((response) => (response.itemList ?? []) as unknown as Array<Record<string, unknown>>)
      .forEach((item) => {
        const spuId = String(item.spuId ?? item.externalItemId ?? '');
        const platform = String(item.platform ?? '').toUpperCase();
        const region = String(item.region ?? '').toUpperCase();
        const image =
          typeof item.imageUrl === 'string'
            ? item.imageUrl
            : Array.isArray(item.imageUrl)
            ? item.imageUrl.find((url) => typeof url === 'string' && url.trim())
            : undefined;
        detailByKey.set(normalizeProductSpuKey(spuId, platform, region), {
          externalStoreId: String(item.externalShopId ?? item.shopId ?? ''),
          itemUrl: item.itemUrl == null ? undefined : String(item.itemUrl),
          imageUrl: image || undefined,
        });
      });
    return {
      ...page,
      items: page.items.map((item) => {
        const detail = detailByKey.get(
          normalizeProductSpuKey(item.externalSpuId, item.platform, item.region),
        );
        // 店铺不一致的回查结果不可信，直接跳过（对齐 perseus 外店守卫）
        if (!detail || detail.externalStoreId !== item.externalStoreId) return item;
        return {
          ...item,
          itemUrl: item.itemUrl || detail.itemUrl || null,
          imageUrl: item.imageUrl || detail.imageUrl || null,
        };
      }),
    };
  } catch {
    return page;
  }
};

export const searchProductKnowledgeProducts = async (input: ProductKnowledgeProductSearchInput) => {
  const page = await knowledgePost<ProductKnowledgeProductPage>(
    `${productBase}/products/search`,
    input,
  );
  return enrichProductPage(page);
};

export const searchProductKnowledge = (input: ProductKnowledgeSearchInput) =>
  knowledgePost<ProductKnowledgeDetailPage>(`${productBase}/products/knowledge/search`, input);

export const searchProductKnowledgeScopeProducts = (input: ProductKnowledgeScopeSearchInput) =>
  knowledgePost<ProductKnowledgeScopeSearchResult>(`${productBase}/scope/products/search`, input);

export const addProductKnowledgeScopeMember = (input: ProductKnowledgeScopeMutationInput) =>
  knowledgePost<ProductKnowledgeScopeSnapshot>(`${productBase}/scope/members/add`, input);

export const removeProductKnowledgeScopeMember = (input: ProductKnowledgeScopeMutationInput) =>
  knowledgePost<ProductKnowledgeScopeSnapshot>(`${productBase}/scope/members/remove`, input);

export const searchProductKnowledgePickerProducts = (input: ProductKnowledgePickerInput) =>
  knowledgePost<ProductKnowledgeScopeSearchResult>(`${productBase}/product-picker/search`, input);

export const getProductKnowledgeDetail = <Type extends ProductKnowledgeType>(
  type: Type,
  knowledgeId: string | number,
) => knowledgeGet<ProductKnowledgeDetailByType[Type]>(getKnowledgePath(type, knowledgeId));

export const updateProductKnowledge = <Type extends ProductKnowledgeType>(
  type: Type,
  knowledgeId: string | number,
  input: ProductKnowledgeUpdateInputByType[Type],
) => knowledgePut<ProductKnowledgeDetailByType[Type]>(getKnowledgePath(type, knowledgeId), input);

/** 统一软删回执状态，契约见 oversea-knowledge docs/api/knowledge-unified-delete-frontend-api.md */
export type KnowledgeDeleteItemStatus = 'DELETED' | 'ALREADY_DELETED' | 'NOT_FOUND' | 'FAILED';

export interface KnowledgeDeleteResult {
  knowledgeId: string | number;
  status: KnowledgeDeleteItemStatus;
  message?: string;
}

/** 统一软删（对齐 perseus deleteKnowledge）：按条目回执，单条失败不中断批次 */
export const deleteKnowledge = (
  type: ProductKnowledgeType,
  knowledgeIds: Array<string | number>,
) =>
  knowledgePost<{ results: KnowledgeDeleteResult[] }>(
    '/oversea-knowledge/knowledge/delete',
    { knowledgeType: type, knowledgeIds },
  );

/**
 * 单条删除封装：DELETED / ALREADY_DELETED（幂等）视为成功，NOT_FOUND / FAILED 抛错。
 */
export const deleteProductKnowledge = async (
  type: ProductKnowledgeType,
  knowledgeId: string | number,
) => {
  const { results } = await deleteKnowledge(type, [knowledgeId]);
  const result = results?.[0];
  if (!result || result.status === 'FAILED' || result.status === 'NOT_FOUND') {
    throw new Error(result?.message || 'delete product knowledge failed');
  }
  return result;
};

export const setProductKnowledgeEnabled = <Type extends ProductKnowledgeType>(
  type: Type,
  knowledgeId: string | number,
  enabled: boolean,
) =>
  knowledgePost<ProductKnowledgeDetailByType[Type]>(
    `${getKnowledgePath(type, knowledgeId)}/${enabled ? 'enable' : 'disable'}`,
  );
